import React, { useState, useEffect } from 'react'
import {
  X,
  Mail,
  Users,
  CheckCircle2,
  Sparkles,
  Send,
  ExternalLink,
} from 'lucide-react'
import {
  apiSubmitPrivateTourEnquiry,
  type PrivateTourEnquiryPayload,
} from '../api/enquiries/enquiryApi'

interface PrivateTourEnquiryModalProps {
  isOpen: boolean
  onClose: () => void
  tourId?: string
  tourTitle: string
  estimatedPrice?: number
  // Optional / backwards-compatibility
  pickupLocation?: string
  departureDate?: string
  departureTime?: string
  adults?: number
  children?: number
  vehicleName?: string
}

export const PrivateTourEnquiryModal: React.FC<PrivateTourEnquiryModalProps> = ({
  isOpen,
  onClose,
  tourId,
  tourTitle,
  estimatedPrice,
}) => {
  const today = new Date().toISOString().split('T')[0]
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]

  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(tomorrow)
  const [travelersCount, setTravelersCount] = useState(2)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Prefill user details if logged in
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false)
      setErrorMessage(null)

      if (!fromDate) setFromDate(today)
      if (!toDate) setToDate(tomorrow)

      try {
        const savedEmail = localStorage.getItem('user_email')
        if (savedEmail) setEmail(savedEmail)

        const rawUser = localStorage.getItem('user_data')
        if (rawUser) {
          const parsed = JSON.parse(rawUser)
          const fullName =
            parsed.name ||
            parsed.full_name ||
            [parsed.first_name, parsed.last_name].filter(Boolean).join(' ')
          if (fullName) setName(fullName)
          if (parsed.phone) setPhone(parsed.phone)
        }
      } catch {}
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    if (toDate && val > toDate) {
      setToDate(val)
    }
  }

  // Create mailto fallback link with prefilled details
  const emailSubject = encodeURIComponent(
    `Private Tour Enquiry: ${tourTitle} - ${name || 'Customer'}`
  )
  const emailBody = encodeURIComponent(
    `Hello Salzburg Cab Team,\n\nI would like to enquire about booking the following Private Tour:\n\n` +
      `• Tour: ${tourTitle}\n` +
      `• Travel Dates: From ${fromDate} to ${toDate}\n` +
      `• Travelers: ${travelersCount} Traveler${travelersCount > 1 ? 's' : ''}\n` +
      (estimatedPrice ? `• Estimated Price: From €${estimatedPrice}\n` : '') +
      `\nCustomer Details:\n` +
      `• Name: ${name}\n` +
      `• Email: ${email}\n` +
      `• Phone: ${phone}\n` +
      (notes ? `• Special Requests: ${notes}\n` : '') +
      `\nPlease send me a detailed quote and availability confirmation.\n\nThank you!`
  )
  const mailtoUrl = `mailto:info@salzburgcab.com?subject=${emailSubject}&body=${emailBody}`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!fromDate) {
      setErrorMessage('Please select a From date.')
      return
    }
    if (!toDate) {
      setErrorMessage('Please select a To date.')
      return
    }
    if (toDate < fromDate) {
      setErrorMessage('To date cannot be earlier than From date.')
      return
    }
    if (travelersCount < 1) {
      setErrorMessage('Please select at least 1 traveler.')
      return
    }
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setErrorMessage('Please fill in all required contact details.')
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    const payload: PrivateTourEnquiryPayload = {
      tour_id: tourId,
      tour_title: tourTitle,
      from_date: fromDate,
      to_date: toDate,
      travelers_count: travelersCount,
      adults: travelersCount,
      children: 0,
      departure_date: fromDate,
      departure_time: 'Flexible',
      estimated_price: estimatedPrice,
      customer_name: name.trim(),
      customer_email: email.trim(),
      customer_phone: phone.trim(),
      notes: notes.trim(),
      recipient_email: 'info@salzburgcab.com',
    }

    try {
      const res = await apiSubmitPrivateTourEnquiry(payload)
      setIsSubmitting(false)

      if (res.success) {
        setIsSuccess(true)
      } else {
        setErrorMessage(res.message || 'Failed to submit enquiry.')
      }
    } catch {
      setIsSubmitting(false)
      setErrorMessage('Failed to submit enquiry. Please try again.')
    }
  }

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-3 sm:p-4 notranslate">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-[4px] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="enquiry-modal-title"
        className="relative max-h-[90dvh] w-full max-w-[520px] overflow-y-auto rounded-[20px] bg-white p-5 sm:p-6 shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-slate-100 z-10 animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>

        {isSuccess ? (
          /* Success Screen */
          <div className="py-6 text-center flex flex-col items-center justify-center animate-in zoom-in-95 duration-300">
            <div className="relative mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-inner">
              <CheckCircle2 className="h-8 w-8 stroke-[2.2]" />
              <div className="absolute -top-1 -right-1">
                <Sparkles className="h-4 w-4 text-amber-400 animate-bounce" />
              </div>
            </div>

            <h3 className="font-host text-[20px] font-bold text-slate-900 tracking-tight">
              Enquiry Sent to Salzburg Cab!
            </h3>
            <p className="mt-1.5 text-[13px] text-slate-600 max-w-sm leading-relaxed">
              Thank you, <span className="font-semibold text-slate-800">{name}</span>. Your enquiry for{' '}
              <span className="font-semibold text-[#FF6A1A]">{tourTitle}</span> has been routed directly to{' '}
              <span className="font-semibold text-slate-800">info@salzburgcab.com</span>.
            </p>

            <div className="mt-3 rounded-xl border border-slate-100 bg-[#F8FAFC] px-4 py-2 text-left w-full max-w-xs text-[12px] text-slate-700 space-y-0.5">
              <div className="flex justify-between">
                <span className="text-slate-400">Dates:</span>
                <span className="font-semibold">{fromDate} to {toDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Travelers:</span>
                <span className="font-semibold">{travelersCount} Traveler{travelersCount > 1 ? 's' : ''}</span>
              </div>
            </div>

            <p className="mt-2 text-[12px] text-slate-400">
              Our travel specialists will respond with a confirmed itinerary shortly.
            </p>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
              <a
                href={mailtoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 font-host text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                <Mail className="h-3.5 w-3.5 text-[#FF6A1A]" />
                Send Copy via Email Client
                <ExternalLink className="h-3 w-3 text-slate-400" />
              </a>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 items-center justify-center rounded-full bg-[#111111] px-5 font-host text-[12px] font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {/* Header */}
            <div className="pr-8">
              <div className="flex items-center gap-2">
                <h2
                  id="enquiry-modal-title"
                  className="font-host text-[19px] sm:text-[21px] font-bold tracking-tight text-slate-900 leading-tight"
                >
                  Enquire About This Tour
                </h2>
                <span className="rounded-full bg-[#FFF2EA] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FF6A1A]">
                  Private Tour
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between text-[12px] font-medium text-slate-500">
                <span className="truncate max-w-[320px] sm:max-w-[380px]">{tourTitle}</span>
                {/* {estimatedPrice && (
                  <span className="shrink-0 font-semibold text-slate-800 ml-2">
                    From €{estimatedPrice}
                  </span>
                )} */}
              </div>
            </div>

            {/* Tour Schedule & Travelers Details */}
            <div className="rounded-xl border border-slate-200/90 bg-[#F8FAFC] p-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div>
                  <label className="block font-host text-[11px] font-semibold text-slate-600 mb-1">
                    From Date *
                  </label>
                  <input
                    type="date"
                    required
                    min={today}
                    value={fromDate}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-host text-[12px] font-medium text-slate-900 focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20 transition"
                  />
                </div>

                <div>
                  <label className="block font-host text-[11px] font-semibold text-slate-600 mb-1">
                    To Date *
                  </label>
                  <input
                    type="date"
                    required
                    min={fromDate || today}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-host text-[12px] font-medium text-slate-900 focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20 transition"
                  />
                </div>

                <div>
                  <label className="block font-host text-[11px] font-semibold text-slate-600 mb-1">
                    Travelers *
                  </label>
                  <div className="flex h-[33px] items-center justify-between rounded-lg border border-slate-200 bg-white px-2 shadow-xs">
                    <button
                      type="button"
                      onClick={() => setTravelersCount((c) => Math.max(1, c - 1))}
                      disabled={travelersCount <= 1}
                      aria-label="Decrease traveler count"
                      className="flex h-6 w-6 select-none items-center justify-center rounded bg-slate-100 text-xs font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      —
                    </button>
                    <div className="flex items-center gap-1 font-host text-[12px] font-bold text-slate-900">
                      <Users className="h-3 w-3 text-[#FF6A1A]" />
                      <span>{travelersCount}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTravelersCount((c) => Math.min(50, c + 1))}
                      aria-label="Increase traveler count"
                      className="flex h-6 w-6 select-none items-center justify-center rounded bg-[#BAD67E] text-xs font-bold text-black transition hover:bg-[#b0d26c]"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="block font-host text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Michael Schmidt"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-host text-[12px] text-slate-900 placeholder-slate-400 focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20 transition"
                />
              </div>

              <div>
                <label className="block font-host text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-host text-[12px] text-slate-900 placeholder-slate-400 focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20 transition"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block font-host text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+43 660 1234567"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-host text-[12px] text-slate-900 placeholder-slate-400 focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20 transition"
                />
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="private-tour-special-requests"
                  className="mb-1 block font-host text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                >
                  Special Requests (optional)
                </label>

                <textarea
                  id="private-tour-special-requests"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Custom stops, child seats, or any other special requests..."
                  className="block min-h-[110px] w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 font-host text-[12px] leading-relaxed text-slate-900 placeholder-slate-400 transition focus:border-[#FF6A1A] focus:outline-none focus:ring-1 focus:ring-[#FF6A1A]/20"
                />
              </div>
            </div>

            {/* Email destination indicator */}
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <span>
                Enquiry will be sent directly to{' '}
                <strong className="font-semibold text-slate-700">info@salzburgcab.com</strong>
              </span>
            </div>

            {errorMessage && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-[12px] text-red-600 font-medium">
                {errorMessage}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
              <a
                href={mailtoUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-medium text-slate-500 hover:text-slate-800 underline underline-offset-2 transition"
              >
                Send via email app
              </a>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-9 rounded-full px-4 font-host text-[12px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#FF6A1A] px-5 font-host text-[13px] font-bold text-white shadow-[0_2px_8px_rgba(255,106,26,0.25)] hover:bg-[#E05B13] active:scale-[0.98] transition disabled:opacity-50"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" />
                      Send Enquiry
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default PrivateTourEnquiryModal
